import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollToTopOnNavigate from '@/components/feature/ScrollToTopOnNavigate';
import { mangaList, chapters } from '@/mocks/manga';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeChapter, setActiveChapter] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  const manga = mangaList.find((m) => m.slug === slug);
  const categoryMap: Record<string, string> = {
    rexue: '热血',
    aiqing: '恋爱',
    qihuan: '奇幻',
    xuanhuan: '玄幻',
    xiju: '喜剧',
    juqing: '剧情',
    dongzuo: '动作',
    xuanyi: '悬疑',
    maoxian: '冒险',
    kongbu: '恐怖',
    xiaoyuan: '校园',
  };

  useEffect(() => {
    setActiveChapter(null);
    setActiveTab('info');
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!manga) {
    return (
      <>
        <ScrollToTopOnNavigate />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4 mx-auto">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <h1 className="text-lg font-medium text-foreground-800 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 免费阅读无删减漫画下拉式 - 吹雪漫画网`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: '2026-01-15',
    dateModified: manga.updateTime,
    image: manga.cover,
    url: `https://chuixue.com/article/${manga.slug}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '吹雪漫画网',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${manga.title}可以在线免费阅读吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的！${manga.title}在吹雪漫画网完全免费阅读，采用下拉式阅读方式，无删减，画质高清，让您畅享阅读体验。`,
        },
      },
      {
        '@type': 'Question',
        name: `${manga.title}目前更新到多少话？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${manga.title}目前已更新至第${manga.chapters}话，状态为${manga.status}，请持续关注吹雪漫画网获取最新更新。`,
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${categoryMap[manga.category] || manga.category}漫画`, href: `/category/${manga.category}` },
    { label: manga.title },
  ];

  return (
    <>
      <ScrollToTopOnNavigate />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Manga Info Header */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Cover */}
              <div className="flex-shrink-0 w-full sm:w-[200px] mx-auto md:mx-0">
                <div className="relative overflow-hidden rounded-lg bg-background-200 aspect-[3/4]">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 吹雪漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {manga.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-3">
                  <strong>作者：</strong>{manga.author}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    评分 {manga.rating}
                  </span>
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {manga.status}
                  </span>
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {categoryMap[manga.category] || manga.category}
                  </span>
                  <span className="px-3 py-1 bg-background-200 text-foreground-600 text-xs font-medium rounded-full whitespace-nowrap">
                    {manga.chapters} 话
                  </span>
                  <span className="px-3 py-1 bg-background-200 text-foreground-600 text-xs font-medium rounded-full whitespace-nowrap">
                    {Math.floor(manga.views / 10000)}万阅读
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {manga.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setActiveChapter(1);
                      setActiveTab('chapters');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    从第一话开始阅读
                  </button>
                  <button
                    onClick={() => {
                      setActiveChapter(chapters[chapters.length - 1].number);
                      setActiveTab('chapters');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-6 py-2.5 bg-background-200 text-foreground-700 font-medium rounded-full hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    阅读最新话
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="w-full px-4 md:px-6 border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-5xl mx-auto flex items-center gap-6">
            <button
              onClick={() => { setActiveTab('info'); setActiveChapter(null); }}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'info'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              漫画详情
            </button>
            <button
              onClick={() => setActiveTab('chapters')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'chapters'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              章节目录
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50 min-h-[400px]">
          <div className="max-w-5xl mx-auto">
            {activeTab === 'info' && (
              <article className="prose prose-sm max-w-none">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">作品简介</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  {manga.description}
                </p>

                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">作品信息</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">作品名：</dt>
                    <dd className="text-foreground-800"><strong>{manga.title}</strong></dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">作者：</dt>
                    <dd className="text-foreground-800">{manga.author}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">分类：</dt>
                    <dd className="text-foreground-800">{categoryMap[manga.category] || manga.category}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">状态：</dt>
                    <dd className="text-foreground-800">{manga.status}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">总话数：</dt>
                    <dd className="text-foreground-800">{manga.chapters} 话</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-foreground-500 w-16 flex-shrink-0">评分：</dt>
                    <dd className="text-primary-600 font-medium">{manga.rating}</dd>
                  </div>
                </dl>

                <h2 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">读者评价</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-primary-500 text-sm font-medium">★★★★★</span>
                      <span className="text-xs text-foreground-500">热心读者</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      画风太赞了！剧情也很吸引人，每一话都让人欲罢不能。强烈推荐！
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-primary-500 text-sm font-medium">★★★★☆</span>
                      <span className="text-xs text-foreground-500">漫画达人</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      人物塑造很细腻，故事情节环环相扣。下拉式阅读很流畅，体验很好。
                    </p>
                  </div>
                </div>
              </article>
            )}

            {activeTab === 'chapters' && (
              <div>
                {/* Reader View */}
                {activeChapter !== null ? (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-heading text-lg font-bold text-foreground-900">
                        第 {activeChapter} 话
                      </h2>
                      <button
                        onClick={() => setActiveChapter(null)}
                        className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                        返回目录
                      </button>
                    </div>

                    {/* Manga Reader - Scroll View */}
                    <div className="max-w-3xl mx-auto bg-background-900 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-background-700/50">
                        <p className="text-sm text-background-300 text-center">
                          下拉式阅读 —— 向下滑动继续阅读
                        </p>
                      </div>

                      {/* Simulated manga pages */}
                      {activeChapter === 1 && (
                        <>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20a%20young%20man%20entering%20a%20mysterious%20old%20shop%20with%20antique%20items%2C%20speech%20bubbles%2C%20screen%20tones%2C%20professional%20manga%20artwork%2C%20clean%20page%20layout%2C%20high%20contrast%20black%20ink%20on%20white%20paper&width=900&height=1280&seq=manga-ch1-p1&orientation=portrait"
                              alt={`${manga.title} 第1话 第1页`}
                              title={`${manga.title} 第1话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20an%20elderly%20shopkeeper%20greeting%20the%20young%20visitor%2C%20antique%20shop%20interior%20with%20shelves%20of%20curious%20items%2C%20speech%20bubbles%2C%20screen%20tones%2C%20professional%20manga%20artwork&width=900&height=1280&seq=manga-ch1-p2&orientation=portrait"
                              alt={`${manga.title} 第1话 第2页`}
                              title={`${manga.title} 第1话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20closeup%20of%20a%20mysterious%20glowing%20object%20on%20a%20wooden%20shelf%2C%20the%20young%20man%20reaching%20out%20with%20wonder%2C%20speech%20bubbles%2C%20screen%20tones%2C%20professional%20manga%20art&width=900&height=1280&seq=manga-ch1-p3&orientation=portrait"
                              alt={`${manga.title} 第1话 第3页`}
                              title={`${manga.title} 第1话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20the%20shop%20transforming%20with%20magical%20energy%2C%20floating%20items%20and%20sparkles%2C%20the%20young%20mans%20shocked%20expression%2C%20speech%20bubbles%2C%20screen%20tones%2C%20professional%20manga&width=900&height=1280&seq=manga-ch1-p4&orientation=portrait"
                              alt={`${manga.title} 第1话 第4页`}
                              title={`${manga.title} 第1话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20the%20young%20man%20accepting%20a%20strange%20key%20from%20the%20shopkeeper%2C%20dramatic%20lighting%20effect%2C%20chapter%20end%20page%20with%20bold%20panel%2C%20speech%20bubbles%2C%20screen%20tones&width=900&height=1280&seq=manga-ch1-p5&orientation=portrait"
                              alt={`${manga.title} 第1话 第5页`}
                              title={`${manga.title} 第1话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                        </>
                      )}
                      {activeChapter !== 1 && (
                        <>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20action%20scene%20with%20two%20characters%20in%20dramatic%20confrontation%2C%20dynamic%20composition%2C%20speech%20bubbles%2C%20screen%20tones%2C%20professional%20manga%20artwork&width=900&height=1280&seq=manga-chX-p1&orientation=portrait"
                              alt={`${manga.title} 第${activeChapter}话 第1页`}
                              title={`${manga.title} 第${activeChapter}话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20dialogue%20scene%20with%20characters%20sitting%20in%20a%20traditional%20room%20with%20tatami%20mats%2C%20speech%20bubbles%2C%20expressive%20faces%2C%20screen%20tones&width=900&height=1280&seq=manga-chX-p2&orientation=portrait"
                              alt={`${manga.title} 第${activeChapter}话 第2页`}
                              title={`${manga.title} 第${activeChapter}话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20emotional%20closeup%20of%20a%20female%20character%20with%20flowing%20hair%2C%20tearful%20expression%2C%20dramatic%20speed%20lines%2C%20screen%20tones%2C%20professional%20manga&width=900&height=1280&seq=manga-chX-p3&orientation=portrait"
                              alt={`${manga.title} 第${activeChapter}话 第3页`}
                              title={`${manga.title} 第${activeChapter}话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20characters%20walking%20through%20a%20mysterious%20forest%20path%20at%20night%2C%20moonlight%20shadows%2C%20speech%20bubbles%2C%20screen%20tones&width=900&height=1280&seq=manga-chX-p4&orientation=portrait"
                              alt={`${manga.title} 第${activeChapter}话 第4页`}
                              title={`${manga.title} 第${activeChapter}话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                          <div className="relative">
                            <img
                              src="https://readdy.ai/api/search-image?query=Manga%20page%20illustration%20in%20black%20and%20white%20Japanese%20comic%20style%2C%20detailed%20line%20art%2C%20cliffhanger%20ending%20with%20a%20mysterious%20shadowy%20figure%20revealing%20themselves%2C%20dramatic%20full-page%20panel%2C%20screen%20tones%2C%20professional%20manga&width=900&height=1280&seq=manga-chX-p5&orientation=portrait"
                              alt={`${manga.title} 第${activeChapter}话 第5页`}
                              title={`${manga.title} 第${activeChapter}话 - 吹雪漫画网`}
                              className="w-full"
                              loading="lazy"
                            />
                          </div>
                        </>
                      )}

                      {/* Page navigation */}
                      <div className="p-6 flex items-center justify-between gap-4 border-t border-background-700/50">
                        <button
                          onClick={() => {
                            if (activeChapter > 1) setActiveChapter(activeChapter - 1);
                          }}
                          disabled={activeChapter <= 1}
                          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                            activeChapter <= 1
                              ? 'bg-background-700/50 text-background-500 cursor-not-allowed'
                              : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          }`}
                        >
                          <i className="ri-arrow-left-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                          上一话
                        </button>
                        <span className="text-sm text-background-400 whitespace-nowrap">
                          第 {activeChapter} / {chapters.length} 话
                        </span>
                        <button
                          onClick={() => {
                            if (activeChapter < chapters.length) setActiveChapter(activeChapter + 1);
                          }}
                          disabled={activeChapter >= chapters.length}
                          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                            activeChapter >= chapters.length
                              ? 'bg-background-700/50 text-background-500 cursor-not-allowed'
                              : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          }`}
                        >
                          下一话
                          <i className="ri-arrow-right-line w-4 h-4 inline-flex items-center justify-center ml-1"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Chapter List */
                  <div>
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                      章节目录（共 {chapters.length} 话）
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {chapters.map((ch) => (
                        <button
                          key={ch.number}
                          onClick={() => {
                            setActiveChapter(ch.number);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-transparent hover:border-primary-200/50 transition-all cursor-pointer text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold flex-shrink-0">
                              {ch.number}
                            </span>
                            <span className="text-sm text-foreground-700 font-medium">{ch.title}</span>
                          </div>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateTime}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}